import CssBaseline from '@mui/material/CssBaseline';
import { type Components, type Theme, createTheme } from '@mui/material/styles';
import MuiThemeProvider from '@mui/material/styles/ThemeProvider';
import { type TypographyOptions } from '@mui/material/styles/createTypography';
import { merge } from 'es-toolkit/compat';
import { type ReactNode, memo, useMemo } from 'react';
import { type ConfigObject, DebugLevel } from '../_configs/types';
import checkUndefinedNullOrEmpty from '../utils/tools/checkUndefinedNullOrEmpty';
import replaceStyleTexts from '../utils/tools/replaceStyleTexts';
import { breakpoints } from './breakpoints';
import { componentsOverrides } from './overrides';
import { palette } from './palette';
import { shadows } from './shadows';
import { typography } from './typography';

type Props = {
    children: ReactNode;
    embed?: boolean;
    forceDark?: boolean;
    rootContainer: HTMLDivElement;
    settings: ConfigObject;
};

function ThemeProvider({ children, embed, forceDark, rootContainer, settings }: Props) {
    const themeMode = useMemo(() => (forceDark || settings?.style?.darkMode ? 'dark' : 'light'), [forceDark, settings?.style?.darkMode]);

    const theme = useMemo<Theme>(() => {
        const t = createTheme({
            ...palette(themeMode, rootContainer, settings?.style?.primaryColor, settings?.style?.secondaryColor),
            breakpoints: {
                values: breakpoints,
            },
            direction: 'ltr',
            rawTypography: typography(true, settings?.style?.fontSize),
            shadows: shadows(themeMode),
            shape: { borderRadius: settings?.style?.borderRadius * 4 },
            typography: typography(false, settings?.style?.fontSize),
        });

        t.typography = merge(
            {},
            t.typography,
            replaceStyleTexts<TypographyOptions>(settings?.style?.muiTypography, {
                captionFontSize: t?.rawTypography?.caption?.fontSize,
                colorPrimary: settings?.style?.primaryColor,
                colorSecondary: settings?.style?.secondaryColor,
                fontFamily: settings?.style?.fontFamily,
                fontSize: settings?.style?.fontSize || 16,
                textPrimary: t?.palette?.text?.primary,
            })
        );

        t.components = merge(
            {},
            componentsOverrides(t),
            replaceStyleTexts<Components<Theme>>(settings?.style?.muiComponents, {
                captionFontSize: t?.rawTypography?.caption?.fontSize,
                colorPrimary: settings?.style?.primaryColor,
                colorSecondary: settings?.style?.secondaryColor,
                fontFamily: settings?.style?.fontFamily,
                fontSize: settings?.style?.fontSize || 16,
                textPrimary: t?.palette?.text?.primary,
            })
        );

        if (checkUndefinedNullOrEmpty(t.typography.fontFamily)) {
            t.typography.body1.fontFamily = settings?.style?.fontFamily;
            t.typography.body2.fontFamily = settings?.style?.fontFamily;
            t.typography.button.fontFamily = settings?.style?.fontFamily;
        }

        if (settings?.general?.debug === DebugLevel.TRACKING || settings?.general?.debug >= DebugLevel.INFO) {
            console.log('theme -', t);
        }

        return t;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return embed ? (
        <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    ) : (
        <MuiThemeProvider theme={theme}>
            <CssBaseline />
            {children}
        </MuiThemeProvider>
    );
}

export default memo(ThemeProvider);
